import React from "react";
import { Route } from "react-router-dom";
import { UserRoleEnum } from "@/types/role.type";
import type { RouteCtx } from "../routeContext";
import { PlaceholderPage, NotFound } from "../shell";
import {
  StudyManagement,
  UserManagement,
  SiteManagement,
  MonitoringTracker,
  VisitDetailsPage,
  EcrfViewerPage,
  FormStatesPage,
  DataReviewPage,
  StudyDashboard,
  SiteDashboard,
  QueriesPage,
  CodeListsPage,
  ExportsPage,
  SiteActivation,
  RegulatoryApprovalsMain,
  ApprovalDetailPage,
  ActivityHistory,
  CRFDesigner,
  CRFTemplates,
  SubjectsManagement,
  VisitDataEntry,
  EDCDataReviewPage,
  DataCompletenessDashboard,
  ExportsEDC,
  DocumentLibrary,
  UploadDocument,
  QCReview,
  ETMFCompletenessDashboard,
  RetentionArchival,
  InspectionMode,
  ModelGovernance,
  MigrationReview,
  AuditLogsRoute,
  FinancePayments,
  IssueRiskTrackerPage,
  QMSPage,
  SiteFeasibilityPage,
  ConnectorServicesPage,
  RBQMCenter,
} from "../lazyPages";

/** tenantRoutes — order preserved for React Router first-match. */
export function tenantRoutes(ctx: RouteCtx): React.ReactNode {
  const {
    userRole,
    selectedStudyId,
    availableStudies,
    hasRbqmAccess,
    currentStudy,
    user,
  } = ctx;

  if (userRole !== UserRoleEnum.TENANT) {
    return null;
  }

  return (
    <>
      {/* Dashboards */}
      <Route
        path="/study-dashboard"
        element={<StudyDashboard currentStudy={currentStudy} />}
      />
      <Route
        path="/site-dashboard"
        element={<SiteDashboard currentStudy={currentStudy} />}
      />
      <Route
        path="/ctms/study-dashboard"
        element={<StudyDashboard currentStudy={currentStudy} />}
      />
      <Route
        path="/ctms/site-dashboard"
        element={<SiteDashboard currentStudy={currentStudy} />}
      />
      <Route path="/activity-history" element={<ActivityHistory />} />

      {/* CTMS Routes */}
      <Route path="/users" element={<UserManagement />} />
      <Route
        path="/studies"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/sites"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/tenant/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/cra/monitor-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/site-activation"
        element={
          <SiteActivation userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/site-feasibility"
        element={
          <SiteFeasibilityPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/ctms/regulatory"
        element={
          <RegulatoryApprovalsMain
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/ctms/regulatory/:studyId/:id"
        element={<ApprovalDetailPage />}
      />
      <Route
        path="/ctms/finance"
        element={
          <FinancePayments userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/risk-tracker"
        element={
          <IssueRiskTrackerPage
            userRole={userRole}
            currentStudy={currentStudy}
          />
        }
      />
      <Route
        path="/ctms/quality-risk"
        element={<QMSPage userRole={userRole} currentStudy={currentStudy} />}
      />
      <Route
        path="/ctms/rbqm-center"
        element={
          hasRbqmAccess ? (
            <RBQMCenter userRole={userRole} currentStudy={currentStudy} />
          ) : (
            <NotFound />
          )
        }
      />
      <Route
        path="/ctms/connector-services"
        element={<ConnectorServicesPage userRole={userRole} />}
      />

      {/* EDC Lite Routes */}
      <Route path="/form-states" element={<FormStatesPage />} />
      <Route path="/ecrf-viewer" element={<EcrfViewerPage />} />
      <Route path="/edc/crf-designer" element={<CRFDesigner />} />
      <Route path="/edc/crf-templates" element={<CRFTemplates />} />
      <Route path="/edc/subjects" element={<SubjectsManagement />} />
      <Route
        path="/edc/visit-data-entry"
        element={<VisitDataEntry userRole={userRole} />}
      />
      <Route path="/edc/data-review" element={<EDCDataReviewPage />} />
      <Route
        path="/edc/data-completeness"
        element={<DataCompletenessDashboard />}
      />
      <Route path="/edc/exports" element={<ExportsEDC />} />

      {/* eTMF Routes */}
      <Route
        path="/etmf/document-library"
        element={
          <DocumentLibrary
            userRole={userRole}
            selectedStudyId={selectedStudyId}
          />
        }
      />
      <Route
        path="/etmf/upload-document"
        element={
          <UploadDocument
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/qc-review"
        element={
          <QCReview userRole={userRole} selectedStudyId={selectedStudyId} />
        }
      />
      <Route
        path="/etmf/completeness-dashboard"
        element={
          <ETMFCompletenessDashboard selectedStudyId={selectedStudyId} />
        }
      />
      <Route path="/etmf/retention-archival" element={<RetentionArchival />} />
      <Route
        path="/etmf/inspection-mode"
        element={
          <InspectionMode
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/model-governance"
        element={
          <ModelGovernance
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />
      <Route
        path="/etmf/migration-review"
        element={
          <MigrationReview
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            currentStudy={
              availableStudies?.find((s) => s.id === selectedStudyId) ?? null
            }
          />
        }
      />

      {/* Audit Logs — API menu path is /admin/audit-logs; keep short alias */}
      <Route path="/audit-logs" element={<AuditLogsRoute user={user} />} />
      <Route
        path="/admin/audit-logs"
        element={<AuditLogsRoute user={user} />}
      />

      {/* Templates & Standards Routes */}
      <Route
        path="/ta/templates/forms"
        element={<PlaceholderPage title="Form Templates" />}
      />
      <Route
        path="/ta/templates/fields"
        element={<PlaceholderPage title="Field Library" />}
      />
      <Route path="/ta/templates/codelists" element={<CodeListsPage />} />
      <Route
        path="/ta/templates/rules"
        element={<PlaceholderPage title="Edit Check Rules" />}
      />
      <Route
        path="/ta/templates/blueprints"
        element={<PlaceholderPage title="Study Blueprints" />}
      />

      {/* Legacy routes for backward compatibility */}
      <Route path="/ctms/user-management" element={<UserManagement />} />
      <Route
        path="/ctms/study-management"
        element={
          <StudyManagement userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/ctms/site-management"
        element={
          <SiteManagement
            userRole={userRole}
            selectedStudyId={selectedStudyId}
            availableStudies={availableStudies}
          />
        }
      />
      <Route
        path="/admin/monitoring-tracker"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/monitoring-visits"
        element={
          <MonitoringTracker userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route
        path="/monitoring-visits/:visitId"
        element={<VisitDetailsPage />}
      />
      <Route
        path="/admin/monitoring-tracker/:visitId"
        element={<VisitDetailsPage />}
      />
      <Route
        path="/tenant/site-activation"
        element={
          <SiteActivation userRole={userRole} currentStudy={currentStudy} />
        }
      />
      <Route path="/edc-lite/ecrf-viewer" element={<EcrfViewerPage />} />
      <Route path="/edc-lite/form-states" element={<FormStatesPage />} />
      <Route path="/cdms/data-review" element={<DataReviewPage />} />
      <Route path="/cdms/queries" element={<QueriesPage />} />
      <Route path="/cdms/code-lists" element={<CodeListsPage />} />
      <Route path="/cdms/exports" element={<ExportsPage />} />
      <Route path="/admin/activity-history" element={<ActivityHistory />} />
      <Route path="/reports" element={<PlaceholderPage title="Reports" />} />
    </>
  );
}
